import { Typography, Form, FormInstance, Button, Alert, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { RecommendationComponent } from '../RecommendationComponent';
import { PredefinedReasonCategory } from '../../../shared/Constants';
import { useCareTeamRecommendationPage } from './useCareTeamRecommendationPage';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';
import useUserSession from '../../../hooks/useUserSession';
import { ALLOWED_ROLES } from '../../../shared/Utils';

const { Text } = Typography;
const { useBreakpoint } = Grid;
interface CareTeamRecommendationCollectionPageProps {
	form: FormInstance;
	submitButtonDisabled: boolean;
	onSubmitClick: () => void;
	onCancelClick: () => void;
}

const SectionHeader = ({
	heading,
	description,
}: {
	heading: string;
	description: string;
}) => {
	return (
		<div className="flex flex-col mb-[8px]">
			<Text className="flex-grow text-primary-blue text-lg font-bold pb-4">
				{heading}
			</Text>
			<Text className="mb-[8px]" type="secondary">
				{description}
			</Text>
		</div>
	);
};

export const CareTeamRecommendationCollectionPage = ({
	form,
	submitButtonDisabled,
	onCancelClick,
	onSubmitClick,
}: CareTeamRecommendationCollectionPageProps) => {
	const { t } = useTranslation();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { selectedRole } = useUserSession();

	const isRoleNotAllowed = !ALLOWED_ROLES.includes(selectedRole as UserRole);

	const canDisplayComments =
		selectedRole === UserRole.CARE_TEAM || isRoleNotAllowed;
	const isNotCareTeam = selectedRole !== UserRole.CARE_TEAM;
	return (
		<div
			className={`flex flex-col min-h-0 bg-white mx-auto rounded-lg sm:mt-0 md:mt-4 border border-solid border-gray-200 ${
				isMobile ? 'w-full p-4' : 'w-3/4 max-w-[50%] p-8'
			}`}
		>
			<SectionHeader
				heading={t('provideCareTeamFinalRecommendation')}
				description={t('pleaseProvideBasedOnAllInterviews')}
			/>
			<Form
				name="basic"
				layout="vertical"
				size="middle"
				labelCol={{ span: 16 }}
				initialValues={{ remember: true }}
				autoComplete="off"
				form={form}
			>
				<RecommendationComponent
					commentsMandatory
					commentsLabel="yourCommentsToSupportAboveRecommendation"
					commentsPlaceholder="commentsAreMandatory"
					form={form}
					category={PredefinedReasonCategory.CARE_TEAM_REJECT}
					displayComments={canDisplayComments}
					disableReview={isNotCareTeam}
					disabled={isRoleNotAllowed}
					displayForwardToCareTeam={false}
				/>
			</Form>
			{isNotCareTeam && (
				<Alert
					showIcon
					message={t('onlyCareTeamIsAllowedToSubmitFeedback')}
					type="warning"
					className="bg-orange-10 mb-3"
				/>
			)}
			<div className="flex flex-row p-2 justify-center">
				<Button onClick={onCancelClick}>{t('cancel')}</Button>
				<Button
					onClick={onSubmitClick}
					type="primary"
					htmlType="submit"
					disabled={submitButtonDisabled || isNotCareTeam}
					className="ml-[16px]"
				>
					{t('submit')}
				</Button>
			</div>
		</div>
	);
};

export const CareTeamRecommendationCollectionPageContainer = () => {
	const { form, onSubmitClick, onCancelClick, submitButtonDisabled } =
		useCareTeamRecommendationPage();

	return (
		<>
			<CareTeamRecommendationCollectionPage
				form={form}
				submitButtonDisabled={submitButtonDisabled}
				onCancelClick={onCancelClick}
				onSubmitClick={onSubmitClick}
			/>
		</>
	);
};
